import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ABSOLUTE_ADDRESS,
  DEFAULT_OG_IMAGE,
  ORGANIZATION_NAME,
  seoByPath,
  serviceCatalog,
  SITE_NAME,
  SITE_URL,
  type SeoConfig,
} from './seoConfig';

const ensureMeta = (selector: string, attrs: Record<string, string>, content: string) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    Object.entries(attrs).forEach(([key, value]) => element?.setAttribute(key, value));
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const ensureLink = (selector: string, attrs: Record<string, string>, href: string) => {
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    Object.entries(attrs).forEach(([key, value]) => element?.setAttribute(key, value));
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

const absoluteUrl = (path: string) => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

const buildOrganizationSchema = () => ({
  '@type': 'Organization',
  name: ORGANIZATION_NAME,
  alternateName: SITE_NAME,
  url: SITE_URL,
  logo: absoluteUrl('/fev.png'),
  email: 'info@grassrootssports.in',
  telephone: '+91 95783 99299',
  address: {
    '@type': 'PostalAddress',
    ...ABSOLUTE_ADDRESS,
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: 'info@grassrootssports.in',
    telephone: '+91 95783 99299',
    availableLanguage: ['English', 'Tamil'],
  },
});

const buildWebsiteSchema = () => ({
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
});

const buildBreadcrumbSchema = (breadcrumbs: NonNullable<SeoConfig['breadcrumbs']>) => ({
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: absoluteUrl(crumb.path),
  })),
});

const buildWebPageSchema = (config: SeoConfig, canonicalUrl: string) => ({
  '@type': 'WebPage',
  name: config.title,
  description: config.description,
  url: canonicalUrl,
  isPartOf: {
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
  },
});

const buildServiceSchema = () =>
  serviceCatalog.map((service) => ({
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: {
      '@type': 'Organization',
      name: ORGANIZATION_NAME,
      url: SITE_URL,
    },
    areaServed: {
      '@type': 'State',
      name: 'Tamil Nadu',
    },
    url: service.url,
  }));

const getSeoConfig = (pathname: string) => seoByPath[pathname] ?? seoByPath['*'];

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const config = getSeoConfig(location.pathname);
    const canonicalUrl = absoluteUrl(location.pathname);
    const imageUrl = absoluteUrl(config.image?.url ?? DEFAULT_OG_IMAGE);
    const imageAlt = config.image?.alt ?? SITE_NAME;

    document.title = config.title;

    ensureMeta('meta[name="description"]', { name: 'description' }, config.description);
    ensureMeta('meta[name="robots"]', { name: 'robots' }, config.robots);
    ensureMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, SITE_NAME);
    ensureMeta('meta[property="og:title"]', { property: 'og:title' }, config.title);
    ensureMeta('meta[property="og:description"]', { property: 'og:description' }, config.description);
    ensureMeta('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
    ensureMeta('meta[property="og:type"]', { property: 'og:type' }, config.type ?? 'website');
    ensureMeta('meta[property="og:image"]', { property: 'og:image' }, imageUrl);
    ensureMeta('meta[property="og:image:alt"]', { property: 'og:image:alt' }, imageAlt);
    ensureMeta('meta[property="og:locale"]', { property: 'og:locale' }, 'en_IN');
    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, config.title);
    ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, config.description);
    ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, imageUrl);

    ensureLink('link[rel="canonical"]', { rel: 'canonical' }, canonicalUrl);

    const graph: Array<Record<string, unknown>> = [buildWebPageSchema(config, canonicalUrl)];

    if (config.includeOrganization) {
      graph.push(buildOrganizationSchema());
    }

    if (config.includeWebsite) {
      graph.push(buildWebsiteSchema());
    }

    if (config.breadcrumbs) {
      graph.push(buildBreadcrumbSchema(config.breadcrumbs));
    }

    if (config.includeServiceData) {
      graph.push(...buildServiceSchema());
    }

    let script = document.getElementById('seo-json-ld') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'seo-json-ld';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': graph,
    });
  }, [location.pathname]);

  return null;
};

export default SeoManager;
